/**
 * VIKIBOX Attribution & Lead Capture Module (Sprint 1 - Block 2, Iteration 4)
 *
 * Architecture & Privacy Compliance:
 * - Version: 1.1
 * - Scope: Storefront-wide capture on landing, form population upon submission,
 *   and reliable conversion capture strictly upon verified server-side success.
 * - Server Success & Deduplication (Block 2 Iteration 4):
 *   * Single canonical route: strictly Shopify.analytics.publish (no DOM/dataLayer bypass).
 *   * Verified server-side success: {% if form.posted_successfully? %}.
 *   * Resilient state machine: in-memory primary concurrency lock with TTL,
 *     runtime memory fallback when sessionStorage unavailable,
 *     isolated occurred_at per unique lead_id, bounded non-resetting retry budget (5 max),
 *     async watchdog timeout for never-settling promises, fail-safe handling of false/rejections.
 *   * Deterministic stable event_id (event_id === lead_id), zero PII.
 * - Consent: Strict fail-closed policy. Zero marketing data (UTMs, landing paths,
 *   referrer hosts, click IDs) is persisted or sent unless marketing_processing_allowed === true.
 * - Consent Normalization:
 *     * 'yes' -> 'yes'
 *     * 'no' -> 'no'
 *     * '' (Shopify default for unset) -> 'unset'
 *     * API missing/exceptions -> 'unknown'
 * - Storage separation:
 *     * Technical pending lead_id: scoped by form_type in sessionStorage.
 *     * Persistent marketing attribution: first-party localStorage ONLY with marketing consent (TTL: 90 days, aligned with Google Ads offline conversion window).
 * - Atomic Snapshots & Channel-isolated Click IDs:
 *     * First-touch and last-touch UTMs are atomic snapshots (no cross-campaign borrowing).
 *     * Click IDs are tracked atomically per advertising platform:
 *         - google_click_ids: gclid, gbraid, wbraid (atomic replacement per Google touch).
 *         - meta_click_ids: fbclid (atomic replacement per Meta touch).
 * - Shopify Customer Privacy API: Integrates via Shopify.loadFeatures('consent-tracking-api').
 * - Sensitive Paths: Excludes /account, /checkout, /cart, /challenge, /password.
 * - Sanitization: Strips control characters (\r, \n, \t, etc.), validates Click IDs strictly.
 */
(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define([], factory);
  } else if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.VikiboxAttribution = factory();
  }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SCHEMA_VERSION = '1.1';
  var STORAGE_KEY = 'vikibox_attribution_v1';
  var TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days (aligned with Google Ads offline conversion window)

  var ALLOWED_UTMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
  ];

  var ALLOWED_CLICK_IDS = [
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid'
  ];

  var SENSITIVE_PATH_PATTERNS = [
    /^\/account(\/.*)?$/i,
    /^\/checkouts?(\/.*)?$/i,
    /^\/cart(\/.*)?$/i,
    /^\/challenge(\/.*)?$/i,
    /^\/password(\/.*)?$/i
  ];

  var CLICK_ID_REGEX = /^[a-zA-Z0-9_\-\.]{5,100}$/;
  var UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  // In-memory runtime cache for raw touch in the current page session
  var runtimeRawTouch = null;

  /**
   * Generates a standard RFC4122 v4 UUID
   */
  function generateUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Sanitizes string: removes control characters and trims length
   */
  function sanitize(val, maxLen) {
    if (val === null || val === undefined) return '';
    var clean = String(val).replace(/[\x00-\x1F\x7F]/g, '').trim();
    var limit = maxLen || 150;
    return clean.length > limit ? clean.substring(0, limit) : clean;
  }

  /**
   * Validates and sanitizes ad click IDs
   */
  function sanitizeClickId(val) {
    if (!val) return '';
    var clean = sanitize(val, 100);
    return CLICK_ID_REGEX.test(clean) ? clean : '';
  }

  /**
   * Checks whether a given path is considered private or sensitive
   */
  function isSensitivePath(pathname) {
    if (!pathname) return false;
    for (var i = 0; i < SENSITIVE_PATH_PATTERNS.length; i++) {
      if (SENSITIVE_PATH_PATTERNS[i].test(pathname)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Parses sanitized referrer hostname (no path, no query, no PII)
   */
  function parseReferrerHost(refStr, currentOrigin) {
    if (!refStr) return '';
    try {
      var refUrl = new URL(refStr);
      if (currentOrigin && refUrl.origin === currentOrigin) {
        return ''; // strip internal self-referrals
      }
      return sanitize(refUrl.hostname.toLowerCase(), 100);
    } catch (e) {
      return '';
    }
  }

  /**
   * Parses sanitized landing path (no query, no hash, no sensitive paths)
   */
  function parseLandingPath(loc) {
    if (!loc) return '/';
    try {
      var pathname = loc.pathname || '/';
      if (isSensitivePath(pathname)) {
        return '/'; // fallback for sensitive checkout/account paths
      }
      return sanitize(pathname, 120);
    } catch (e) {
      return '/';
    }
  }

  /**
   * Reads allowed marketing parameters from query string
   */
  function parseQueryParams(searchStr) {
    var result = {
      utms: {},
      click_ids: {}
    };
    if (!searchStr) return result;
    try {
      var params = new URLSearchParams(searchStr);
      for (var i = 0; i < ALLOWED_UTMS.length; i++) {
        var uKey = ALLOWED_UTMS[i];
        if (params.has(uKey)) {
          var uVal = params.get(uKey);
          if (uVal) {
            result.utms[uKey] = sanitize(uVal, 100);
          }
        }
      }
      for (var j = 0; j < ALLOWED_CLICK_IDS.length; j++) {
        var cKey = ALLOWED_CLICK_IDS[j];
        if (params.has(cKey)) {
          var cVal = params.get(cKey);
          var cleanClickId = sanitizeClickId(cVal);
          if (cleanClickId) {
            result.click_ids[cKey] = cleanClickId;
          }
        }
      }
    } catch (e) {}
    return result;
  }

  /**
   * Safely accesses localStorage
   */
  function getLocalStorage() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return window.localStorage;
      }
    } catch (e) {}
    return null;
  }

  /**
   * Safely accesses sessionStorage
   */
  function getSessionStorage() {
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        return window.sessionStorage;
      }
    } catch (e) {}
    return null;
  }

  /**
   * Inspects Shopify Customer Privacy state accurately with official normalization
   */
  function inspectConsentState() {
    var marketingAllowed = false;
    var analyticsAllowed = false;
    var visitorMarketing = 'unknown';
    var observedAt = new Date().toISOString();

    if (typeof window !== 'undefined' && window.Shopify && window.Shopify.customerPrivacy) {
      var cp = window.Shopify.customerPrivacy;
      try {
        if (typeof cp.marketingAllowed === 'function') {
          marketingAllowed = cp.marketingAllowed() === true;
        }
        if (typeof cp.analyticsProcessingAllowed === 'function') {
          analyticsAllowed = cp.analyticsProcessingAllowed() === true;
        }
        if (typeof cp.currentVisitorConsent === 'function') {
          var current = cp.currentVisitorConsent();
          if (current && typeof current.marketing !== 'undefined') {
            var raw = String(current.marketing).trim().toLowerCase();
            if (raw === 'yes') {
              visitorMarketing = 'yes';
            } else if (raw === 'no') {
              visitorMarketing = 'no';
            } else if (raw === '') {
              visitorMarketing = 'unset';
            } else {
              visitorMarketing = 'unknown';
            }
          }
        }
      } catch (e) {}
    }

    return {
      marketing_processing_allowed: marketingAllowed ? 'true' : 'false',
      analytics_processing_allowed: analyticsAllowed ? 'true' : 'false',
      visitor_consent_marketing: visitorMarketing,
      consent_observed_at: observedAt,
      consent_schema_version: SCHEMA_VERSION
    };
  }

  /**
   * Purges stored marketing data from localStorage
   */
  function purgeStoredAttribution() {
    var ls = getLocalStorage();
    if (ls) {
      try {
        ls.removeItem(STORAGE_KEY);
      } catch (e) {}
    }
  }

  /**
   * Loads persisted attribution if consent allows and TTL is valid
   */
  function loadStoredAttribution() {
    var consent = inspectConsentState();
    if (consent.marketing_processing_allowed !== 'true') {
      purgeStoredAttribution();
      return null;
    }
    var ls = getLocalStorage();
    if (!ls) return null;
    try {
      var raw = ls.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return null;

      // TTL validation (30 days)
      if (!data.expires_at || typeof data.expires_at !== 'number' || Date.now() > data.expires_at) {
        purgeStoredAttribution();
        return null;
      }
      return data;
    } catch (e) {
      purgeStoredAttribution();
      return null;
    }
  }

  /**
   * Saves atomic attribution snapshots to localStorage with 30-day TTL
   */
  function saveStoredAttribution(data) {
    var consent = inspectConsentState();
    if (consent.marketing_processing_allowed !== 'true') {
      purgeStoredAttribution();
      return false;
    }
    var ls = getLocalStorage();
    if (!ls || !data) return false;
    try {
      data.expires_at = Date.now() + TTL_MS;
      data.schema_version = SCHEMA_VERSION;
      ls.setItem(STORAGE_KEY, JSON.stringify(data));
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Form-scoped technical pending lead_id in sessionStorage
   */
  function getOrCreateLeadId(formType) {
    var key = 'vikibox_pending_lead_id:' + (formType || 'default');
    var ss = getSessionStorage();
    if (ss) {
      try {
        var existing = ss.getItem(key);
        if (existing && UUID_REGEX.test(existing)) {
          return existing;
        }
        var newId = generateUUID();
        ss.setItem(key, newId);
        return newId;
      } catch (e) {}
    }
    return generateUUID();
  }

  /**
   * Clears technical pending lead_id for a specific form after confirmed submission
   */
  function clearPendingLead(formType) {
    var key = 'vikibox_pending_lead_id:' + (formType || 'default');
    var ss = getSessionStorage();
    if (ss) {
      try {
        ss.removeItem(key);
      } catch (e) {}
    }
  }

  /**
   * Creates an atomic touchpoint snapshot from the current URL and document
   */
  function captureTouchSnapshot() {
    var loc = typeof window !== 'undefined' ? window.location : null;
    var doc = typeof document !== 'undefined' ? document : null;
    var currentOrigin = loc ? loc.origin : '';
    var searchStr = loc ? loc.search : '';
    var refStr = doc ? doc.referrer : '';

    var parsed = parseQueryParams(searchStr);
    var referrerHost = parseReferrerHost(refStr, currentOrigin);
    var landingPath = parseLandingPath(loc);
    var nowIso = new Date().toISOString();

    var utms = parsed.utms;
    var clickIds = parsed.click_ids;

    var hasGoogleClick = !!(clickIds.gclid || clickIds.gbraid || clickIds.wbraid);
    var hasMetaClick = !!clickIds.fbclid;

    var hasCampaignParams = !!(
      utms.utm_source ||
      utms.utm_medium ||
      utms.utm_campaign ||
      utms.utm_term ||
      utms.utm_content ||
      hasGoogleClick ||
      hasMetaClick
    );

    return {
      utm_source: utms.utm_source || '',
      utm_medium: utms.utm_medium || '',
      utm_campaign: utms.utm_campaign || '',
      utm_term: utms.utm_term || '',
      utm_content: utms.utm_content || '',
      landing_path: landingPath,
      referrer_host: referrerHost,
      touch_at: nowIso,
      gclid: clickIds.gclid || '',
      gbraid: clickIds.gbraid || '',
      wbraid: clickIds.wbraid || '',
      fbclid: clickIds.fbclid || '',
      hasGoogleClick: hasGoogleClick,
      hasMetaClick: hasMetaClick,
      hasCampaignParams: hasCampaignParams
    };
  }

  /**
   * Main attribution recording logic with platform-isolated Click IDs
   */
  function recordTouchpoint() {
    var touch = captureTouchSnapshot();
    runtimeRawTouch = touch;

    var consent = inspectConsentState();

    // If marketing is NOT allowed, ensure stored data is purged and do NOT write anything
    if (consent.marketing_processing_allowed !== 'true') {
      purgeStoredAttribution();
      return;
    }

    // Marketing IS allowed: load or initialize persistent store
    var stored = loadStoredAttribution();

    if (!stored) {
      // First touch initialization
      var initialData = {
        first_touch: {
          utm_source: touch.utm_source,
          utm_medium: touch.utm_medium,
          utm_campaign: touch.utm_campaign,
          utm_term: touch.utm_term,
          utm_content: touch.utm_content,
          landing_path: touch.landing_path,
          referrer_host: touch.referrer_host,
          touch_at: touch.touch_at
        },
        // Last touch starts identical to first touch
        last_touch: {
          utm_source: touch.utm_source,
          utm_medium: touch.utm_medium,
          utm_campaign: touch.utm_campaign,
          utm_term: touch.utm_term,
          utm_content: touch.utm_content,
          landing_path: touch.landing_path,
          referrer_host: touch.referrer_host,
          touch_at: touch.touch_at
        },
        // Isolated Click IDs per advertising platform
        google_click_ids: {
          gclid: touch.gclid,
          gbraid: touch.gbraid,
          wbraid: touch.wbraid
        },
        meta_click_ids: {
          fbclid: touch.fbclid
        }
      };
      saveStoredAttribution(initialData);
    } else {
      // Update last touch atomically if this touch contains campaign parameters
      if (touch.hasCampaignParams) {
        var firstHasCampaign = !!(
          stored.first_touch && (
            stored.first_touch.utm_source ||
            stored.first_touch.utm_medium ||
            stored.first_touch.utm_campaign ||
            stored.first_touch.utm_term ||
            stored.first_touch.utm_content
          )
        );
        var storedHasClickId = !!(
          (stored.google_click_ids && (stored.google_click_ids.gclid || stored.google_click_ids.gbraid || stored.google_click_ids.wbraid)) ||
          (stored.meta_click_ids && stored.meta_click_ids.fbclid)
        );

        // If stored first_touch had no campaign params or click IDs (was direct/organic), promote this first campaign touch to first_touch
        if (!firstHasCampaign && !storedHasClickId) {
          stored.first_touch = {
            utm_source: touch.utm_source,
            utm_medium: touch.utm_medium,
            utm_campaign: touch.utm_campaign,
            utm_term: touch.utm_term,
            utm_content: touch.utm_content,
            landing_path: touch.landing_path,
            referrer_host: touch.referrer_host,
            touch_at: touch.touch_at
          };
        }

        stored.last_touch = {
          utm_source: touch.utm_source,
          utm_medium: touch.utm_medium,
          utm_campaign: touch.utm_campaign,
          utm_term: touch.utm_term,
          utm_content: touch.utm_content,
          landing_path: touch.landing_path,
          referrer_host: touch.referrer_host,
          touch_at: touch.touch_at
        };

        // Platform-level atomic replacement:
        // If this touch contains Google click IDs, replace the entire Google set atomically
        if (touch.hasGoogleClick) {
          stored.google_click_ids = {
            gclid: touch.gclid,
            gbraid: touch.gbraid,
            wbraid: touch.wbraid
          };
        }

        // If this touch contains Meta click ID, replace Meta set atomically
        if (touch.hasMetaClick) {
          stored.meta_click_ids = {
            fbclid: touch.fbclid
          };
        }

        saveStoredAttribution(stored);
      }
    }
  }

  /**
   * Assembles the full unified attribution payload for a form
   *
   * @param {string} formType ('compatibility' | 'custom_order')
   */
  function getAttributionPayload(formType) {
    var consent = inspectConsentState();
    var leadId = getOrCreateLeadId(formType);
    var nowIso = new Date().toISOString();

    // Default safe payload: technical fields populated, marketing fields strictly EMPTY
    var payload = {
      lead_id: leadId,
      submitted_at: nowIso,
      attribution_schema_version: SCHEMA_VERSION,
      first_utm_source: '',
      first_utm_medium: '',
      first_utm_campaign: '',
      first_utm_term: '',
      first_utm_content: '',
      first_landing_path: '',
      first_referrer_host: '',
      first_touch_at: '',
      last_utm_source: '',
      last_utm_medium: '',
      last_utm_campaign: '',
      last_utm_term: '',
      last_utm_content: '',
      last_landing_path: '',
      last_referrer_host: '',
      last_touch_at: '',
      gclid: '',
      gbraid: '',
      wbraid: '',
      fbclid: '',
      marketing_processing_allowed: consent.marketing_processing_allowed,
      analytics_processing_allowed: consent.analytics_processing_allowed,
      visitor_consent_marketing: consent.visitor_consent_marketing,
      consent_observed_at: consent.consent_observed_at,
      consent_schema_version: consent.consent_schema_version
    };

    // FAIL-CLOSED: only populate marketing data if marketing_processing_allowed === 'true'
    if (consent.marketing_processing_allowed === 'true') {
      var stored = loadStoredAttribution();
      if (stored) {
        if (stored.first_touch) {
          payload.first_utm_source = stored.first_touch.utm_source || '';
          payload.first_utm_medium = stored.first_touch.utm_medium || '';
          payload.first_utm_campaign = stored.first_touch.utm_campaign || '';
          payload.first_utm_term = stored.first_touch.utm_term || '';
          payload.first_utm_content = stored.first_touch.utm_content || '';
          payload.first_landing_path = stored.first_touch.landing_path || '';
          payload.first_referrer_host = stored.first_touch.referrer_host || '';
          payload.first_touch_at = stored.first_touch.touch_at || '';
        }
        if (stored.last_touch) {
          payload.last_utm_source = stored.last_touch.utm_source || '';
          payload.last_utm_medium = stored.last_touch.utm_medium || '';
          payload.last_utm_campaign = stored.last_touch.utm_campaign || '';
          payload.last_utm_term = stored.last_touch.utm_term || '';
          payload.last_utm_content = stored.last_touch.utm_content || '';
          payload.last_landing_path = stored.last_touch.landing_path || '';
          payload.last_referrer_host = stored.last_touch.referrer_host || '';
          payload.last_touch_at = stored.last_touch.touch_at || '';
        }
        // Channel-isolated platform click IDs
        if (stored.google_click_ids) {
          payload.gclid = stored.google_click_ids.gclid || '';
          payload.gbraid = stored.google_click_ids.gbraid || '';
          payload.wbraid = stored.google_click_ids.wbraid || '';
        } else if (stored.click_ids) {
          payload.gclid = stored.click_ids.gclid || '';
          payload.gbraid = stored.click_ids.gbraid || '';
          payload.wbraid = stored.click_ids.wbraid || '';
        }
        if (stored.meta_click_ids) {
          payload.fbclid = stored.meta_click_ids.fbclid || '';
        } else if (stored.click_ids) {
          payload.fbclid = stored.click_ids.fbclid || '';
        }
      }
    }

    return payload;
  }

  /**
   * Sets or updates a hidden input field within the form
   */
  function setHiddenInput(form, name, value) {
    if (!form || !name) return;
    var input = form.querySelector('input[name="' + name + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value || '';
  }

  /**
   * Generates formatted text summary for contact[body]
   */
  function formatAttributionSummary(payload, formType, formVersion) {
    var lines = [
      '\n--- TECHNISCHE ANGABEN (ATTRIBUTION) ---',
      'Lead-ID: ' + (payload.lead_id || '-'),
      'Formulartyp: ' + (formType || '-') + ' (v' + (formVersion || '-') + ')',
      'Schema: v' + (payload.attribution_schema_version || '-'),
      'Status: MarketingAllowed=' + payload.marketing_processing_allowed +
        ', AnalyticsAllowed=' + payload.analytics_processing_allowed +
        ' (VisitorChoice=' + payload.visitor_consent_marketing + ')'
    ];

    if (payload.marketing_processing_allowed === 'true') {
      lines.push(
        'First Touch: ' + (payload.first_utm_source ? 'source=' + payload.first_utm_source : '') +
          (payload.first_utm_medium ? ' medium=' + payload.first_utm_medium : '') +
          (payload.first_utm_campaign ? ' campaign=' + payload.first_utm_campaign : '') +
          (payload.first_landing_path ? ' path=' + payload.first_landing_path : '') +
          (payload.first_referrer_host ? ' ref=' + payload.first_referrer_host : ''),
        'Last Touch: ' + (payload.last_utm_source ? 'source=' + payload.last_utm_source : '') +
          (payload.last_utm_medium ? ' medium=' + payload.last_utm_medium : '') +
          (payload.last_utm_campaign ? ' campaign=' + payload.last_utm_campaign : '') +
          (payload.last_landing_path ? ' path=' + payload.last_landing_path : '') +
          (payload.last_referrer_host ? ' ref=' + payload.last_referrer_host : ''),
        'Click IDs (letzte je Plattform): ' +
          (payload.gclid ? 'gclid=' + payload.gclid + ' ' : '') +
          (payload.gbraid ? 'gbraid=' + payload.gbraid + ' ' : '') +
          (payload.wbraid ? 'wbraid=' + payload.wbraid + ' ' : '') +
          (payload.fbclid ? 'fbclid=' + payload.fbclid + ' ' : '')
      );
    } else {
      lines.push('Marketing-Attribution: [nicht erfasst - kein Marketing-Einverständnis]');
    }

    lines.push('Beobachtungszeitpunkt: ' + payload.consent_observed_at);
    return lines.join('\n');
  }

  /**
   * Populates all 30 technical attribution fields in the specified form
   */
  function populateForm(formElement, formType, formVersion) {
    if (!formElement) return null;
    recordTouchpoint(); // ensure fresh evaluation
    var fType = formType || 'compatibility';
    var fVersion = formVersion || '1.0';
    var payload = getAttributionPayload(fType);

    // 1. Technical fields (5)
    setHiddenInput(formElement, 'contact[lead_id]', payload.lead_id);
    setHiddenInput(formElement, 'contact[form_type]', fType);
    setHiddenInput(formElement, 'contact[form_version]', fVersion);
    setHiddenInput(formElement, 'contact[submitted_at]', payload.submitted_at);
    setHiddenInput(formElement, 'contact[attribution_schema_version]', payload.attribution_schema_version);

    // 2. First Touch (8)
    setHiddenInput(formElement, 'contact[first_utm_source]', payload.first_utm_source);
    setHiddenInput(formElement, 'contact[first_utm_medium]', payload.first_utm_medium);
    setHiddenInput(formElement, 'contact[first_utm_campaign]', payload.first_utm_campaign);
    setHiddenInput(formElement, 'contact[first_utm_term]', payload.first_utm_term);
    setHiddenInput(formElement, 'contact[first_utm_content]', payload.first_utm_content);
    setHiddenInput(formElement, 'contact[first_landing_path]', payload.first_landing_path);
    setHiddenInput(formElement, 'contact[first_referrer_host]', payload.first_referrer_host);
    setHiddenInput(formElement, 'contact[first_touch_at]', payload.first_touch_at);

    // 3. Last Touch (8)
    setHiddenInput(formElement, 'contact[last_utm_source]', payload.last_utm_source);
    setHiddenInput(formElement, 'contact[last_utm_medium]', payload.last_utm_medium);
    setHiddenInput(formElement, 'contact[last_utm_campaign]', payload.last_utm_campaign);
    setHiddenInput(formElement, 'contact[last_utm_term]', payload.last_utm_term);
    setHiddenInput(formElement, 'contact[last_utm_content]', payload.last_utm_content);
    setHiddenInput(formElement, 'contact[last_landing_path]', payload.last_landing_path);
    setHiddenInput(formElement, 'contact[last_referrer_host]', payload.last_referrer_host);
    setHiddenInput(formElement, 'contact[last_touch_at]', payload.last_touch_at);

    // 4. Click IDs (4)
    setHiddenInput(formElement, 'contact[gclid]', payload.gclid);
    setHiddenInput(formElement, 'contact[gbraid]', payload.gbraid);
    setHiddenInput(formElement, 'contact[wbraid]', payload.wbraid);
    setHiddenInput(formElement, 'contact[fbclid]', payload.fbclid);

    // 5. Consent & Privacy fields (5) (Total: exactly 30 fields)
    setHiddenInput(formElement, 'contact[marketing_processing_allowed]', payload.marketing_processing_allowed);
    setHiddenInput(formElement, 'contact[analytics_processing_allowed]', payload.analytics_processing_allowed);
    setHiddenInput(formElement, 'contact[visitor_consent_marketing]', payload.visitor_consent_marketing);
    setHiddenInput(formElement, 'contact[consent_observed_at]', payload.consent_observed_at);
    setHiddenInput(formElement, 'contact[consent_schema_version]', payload.consent_schema_version);

    return {
      payload: payload,
      summaryText: formatAttributionSummary(payload, fType, fVersion)
    };
  }

  var activeInFlight = {};
  var memoryServerSuccess = {};
  var memoryRegisteredEvents = {};
  var autoRetryTimer = null;
  var autoRetryAttempts = 0;
  var MAX_RETRY_ATTEMPTS = (typeof window !== 'undefined' && window.__VIKIBOX_MAX_RETRY_ATTEMPTS) || 5;
  var RETRY_INTERVAL_MS = (typeof window !== 'undefined' && window.__VIKIBOX_RETRY_INTERVAL_MS) || 1000;
  var ASYNC_WATCHDOG_TIMEOUT_MS = (typeof window !== 'undefined' && window.__VIKIBOX_WATCHDOG_TIMEOUT_MS) || 3000;
  var IN_FLIGHT_TTL_MS = 10000;

  function stopAutoRetry() {
    if (autoRetryTimer) {
      clearTimeout(autoRetryTimer);
      clearInterval(autoRetryTimer);
      autoRetryTimer = null;
    }
  }

  function scheduleServerSuccessRetry() {
    if (autoRetryTimer) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    var marker = document.querySelector('[data-vb-server-success]');
    if (!marker || autoRetryAttempts >= MAX_RETRY_ATTEMPTS) {
      stopAutoRetry();
      return;
    }

    autoRetryTimer = setTimeout(function () {
      autoRetryTimer = null;
      var currentMarker = document.querySelector('[data-vb-server-success]');
      if (!currentMarker || autoRetryAttempts >= MAX_RETRY_ATTEMPTS) {
        stopAutoRetry();
        return;
      }

      checkServerSuccess();
    }, RETRY_INTERVAL_MS);
  }

  /**
   * Publishes the unified vikibox:lead_submitted event strictly to Shopify event bus upon verified server success
   *
   * @param {string} formType ('compatibility' | 'custom_order')
   * @param {Object} options ({ form_version, lead_type, solution_type, vehicle_category })
   * @returns {Object|Promise<Object>} Result with { success, registered, status, lead_id, event_id, payload }
   */
  function publishLeadSubmitted(formType, options) {
    var fType = formType || 'compatibility';
    var opts = options || {};
    var ss = getSessionStorage();

    // 1. Identify and resolve stable lead_id and occurred_at
    var pendingKey = 'vikibox_pending_lead_id:' + fType;
    var successKey = 'vikibox_server_success_lead:' + fType;
    var leadId = null;
    var occurredAt = null;

    if (ss) {
      try {
        var p = ss.getItem(pendingKey);
        if (p && UUID_REGEX.test(p)) {
          leadId = p;
        }
      } catch (e) {}
    }

    var storedSuccess = null;
    if (ss) {
      try {
        var rawSuccess = ss.getItem(successKey);
        if (rawSuccess) {
          if (rawSuccess.charAt(0) === '{') {
            storedSuccess = JSON.parse(rawSuccess);
          } else if (UUID_REGEX.test(rawSuccess)) {
            storedSuccess = { lead_id: rawSuccess };
          }
        }
      } catch (e) {}
    }

    if (!storedSuccess && memoryServerSuccess[fType]) {
      storedSuccess = memoryServerSuccess[fType];
    }

    if (leadId) {
      // Reuse occurred_at ONLY IF storedSuccess matches this EXACT leadId!
      if (storedSuccess && storedSuccess.lead_id === leadId && storedSuccess.occurred_at) {
        occurredAt = storedSuccess.occurred_at;
      }
    } else {
      // No active pending lead: check if we have a previous confirmed server success for this form
      if (storedSuccess && storedSuccess.lead_id && UUID_REGEX.test(storedSuccess.lead_id)) {
        leadId = storedSuccess.lead_id;
        occurredAt = storedSuccess.occurred_at || null;
      }
    }

    if (!leadId || !UUID_REGEX.test(leadId)) {
      leadId = generateUUID();
    }

    if (!occurredAt) {
      occurredAt = new Date().toISOString();
    }

    // Save state in memory fallback
    memoryServerSuccess[fType] = {
      lead_id: leadId,
      occurred_at: occurredAt
    };

    // Save state in storage
    if (ss) {
      try {
        ss.setItem(successKey, JSON.stringify(memoryServerSuccess[fType]));
      } catch (e) {}
    }

    // 2. Deterministic stable event_id across all retries for this lead
    var eventId = leadId;
    var registeredKey = 'vikibox_registered_event:' + eventId;
    var inFlightKey = 'vikibox_inflight_event:' + eventId;

    var payload = {
      event_id: eventId,
      lead_id: leadId,
      form_type: fType,
      form_version: sanitize(opts.form_version || '1.0', 20),
      lead_type: sanitize(opts.lead_type || fType, 50),
      solution_type: sanitize(opts.solution_type || 'default', 50),
      vehicle_category: sanitize(opts.vehicle_category || 'default', 50),
      occurred_at: occurredAt
    };

    // 3. Deduplication Check: already registered in Shopify event bus?
    var isRegistered = !!memoryRegisteredEvents[eventId];
    if (!isRegistered && ss) {
      try {
        if (ss.getItem(registeredKey)) {
          isRegistered = true;
        }
      } catch (e) {}
    }

    if (isRegistered) {
      stopAutoRetry();
      return {
        success: true,
        registered: true,
        status: 'already_registered',
        lead_id: leadId,
        event_id: eventId,
        payload: payload
      };
    }

    // 4. In-flight concurrency lock
    var nowMs = Date.now();
    // Primary: In-memory lock with timestamp TTL
    if (activeInFlight[eventId]) {
      if (nowMs - activeInFlight[eventId] < IN_FLIGHT_TTL_MS) {
        return {
          success: false,
          registered: false,
          status: 'in_flight',
          lead_id: leadId,
          event_id: eventId
        };
      } else {
        delete activeInFlight[eventId];
      }
    }

    // Secondary: SessionStorage lock with 10s TTL
    if (ss) {
      try {
        var rawLock = ss.getItem(inFlightKey);
        if (rawLock) {
          var lockTs = parseInt(rawLock, 10);
          if (lockTs && (nowMs - lockTs < IN_FLIGHT_TTL_MS)) {
            return {
              success: false,
              registered: false,
              status: 'in_flight',
              lead_id: leadId,
              event_id: eventId
            };
          } else {
            ss.removeItem(inFlightKey);
          }
        }
      } catch (e) {}
    }

    // 5. Canonical delivery check: Shopify Analytics MUST be available
    if (typeof window === 'undefined' || !window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') {
      return {
        success: false,
        registered: false,
        status: 'analytics_unavailable',
        lead_id: leadId,
        event_id: eventId,
        payload: payload
      };
    }

    // Set lock
    activeInFlight[eventId] = Date.now();
    if (ss) {
      try {
        ss.setItem(inFlightKey, String(Date.now()));
      } catch (e) {}
    }

    function markSuccess() {
      delete activeInFlight[eventId];
      stopAutoRetry();
      memoryRegisteredEvents[eventId] = occurredAt;
      if (ss) {
        try {
          ss.setItem(registeredKey, occurredAt);
          ss.removeItem(inFlightKey);
        } catch (e) {}
      }
      clearPendingLead(fType);
    }

    function markFailure() {
      delete activeInFlight[eventId];
      if (ss) {
        try {
          ss.removeItem(inFlightKey);
        } catch (e) {}
      }
    }

    // 6. Canonical execution to Shopify.analytics.publish with Async Watchdog
    try {
      var publishResult = window.Shopify.analytics.publish('vikibox:lead_submitted', payload);

      if (publishResult && typeof publishResult.then === 'function') {
        var settled = false;
        var watchdogTimer = null;

        var timeoutPromise = new Promise(function (resolve) {
          watchdogTimer = setTimeout(function () {
            if (!settled) {
              resolve({ __isTimeout: true });
            }
          }, ASYNC_WATCHDOG_TIMEOUT_MS);
        });

        return Promise.race([
          publishResult.then(function (val) {
            settled = true;
            if (watchdogTimer) clearTimeout(watchdogTimer);
            return { hasError: false, val: val };
          }).catch(function (err) {
            settled = true;
            if (watchdogTimer) clearTimeout(watchdogTimer);
            return { hasError: true, err: err };
          }),
          timeoutPromise
        ]).then(function (outcome) {
          if (outcome && outcome.__isTimeout) {
            markFailure();
            return {
              success: false,
              registered: false,
              status: 'publish_timeout',
              lead_id: leadId,
              event_id: eventId,
              payload: payload
            };
          }

          if (outcome && outcome.hasError) {
            markFailure();
            return {
              success: false,
              registered: false,
              status: 'publish_error',
              error: outcome.err !== undefined ? String(outcome.err) : 'Promise rejected with non-truthy value',
              lead_id: leadId,
              event_id: eventId,
              payload: payload
            };
          }

          if (outcome.val === false) {
            markFailure();
            return {
              success: false,
              registered: false,
              status: 'publish_rejected_false',
              lead_id: leadId,
              event_id: eventId,
              payload: payload
            };
          }

          markSuccess();
          return {
            success: true,
            registered: true,
            status: 'published',
            lead_id: leadId,
            event_id: eventId,
            payload: payload
          };
        });
      } else {
        if (publishResult === false) {
          markFailure();
          return {
            success: false,
            registered: false,
            status: 'publish_returned_false',
            lead_id: leadId,
            event_id: eventId,
            payload: payload
          };
        }
        markSuccess();
        return {
          success: true,
          registered: true,
          status: 'published',
          lead_id: leadId,
          event_id: eventId,
          payload: payload
        };
      }
    } catch (err) {
      markFailure();
      return {
        success: false,
        registered: false,
        status: 'publish_error',
        error: String(err),
        lead_id: leadId,
        event_id: eventId,
        payload: payload
      };
    }
  }

  /**
   * Scans document for server-rendered success markers and triggers lead publication
   */
  function checkServerSuccess() {
    if (typeof document === 'undefined') return null;
    try {
      var successEl = document.querySelector('[data-vb-server-success]');
      if (!successEl) return null;

      var fType = successEl.getAttribute('data-vb-server-success') || 'compatibility';
      var pendingKey = 'vikibox_pending_lead_id:' + fType;
      var ss = getSessionStorage();
      var checkLeadId = null;
      if (ss) {
        try {
          var p = ss.getItem(pendingKey);
          if (p && UUID_REGEX.test(p)) checkLeadId = p;
        } catch (e) {}
      }
      if (!checkLeadId && memoryServerSuccess[fType]) {
        checkLeadId = memoryServerSuccess[fType].lead_id;
      }

      var isAlreadyRegistered = false;
      if (checkLeadId) {
        if (memoryRegisteredEvents[checkLeadId]) {
          isAlreadyRegistered = true;
        } else if (ss) {
          try {
            if (ss.getItem('vikibox_registered_event:' + checkLeadId)) {
              isAlreadyRegistered = true;
            }
          } catch (e) {}
        }
      }

      if (checkLeadId && isAlreadyRegistered) {
        stopAutoRetry();
        return {
          success: true,
          registered: true,
          status: 'already_registered',
          lead_id: checkLeadId,
          event_id: checkLeadId
        };
      }

      if (autoRetryAttempts >= MAX_RETRY_ATTEMPTS) {
        stopAutoRetry();
        return null;
      }

      autoRetryAttempts++;

      var fVer = successEl.getAttribute('data-form-version') || '1.0';
      var lType = successEl.getAttribute('data-lead-type') || fType;
      var sType = successEl.getAttribute('data-solution-type') || 'default';
      var vCat = successEl.getAttribute('data-vehicle-category') || 'default';
      var res = publishLeadSubmitted(fType, {
        form_version: fVer,
        lead_type: lType,
        solution_type: sType,
        vehicle_category: vCat
      });

      if (res && res.status === 'in_flight') {
        autoRetryAttempts--;
        return res;
      }

      if (res && typeof res.then === 'function') {
        res.then(function (asyncRes) {
          if (asyncRes && asyncRes.registered) {
            stopAutoRetry();
          } else if (autoRetryAttempts < MAX_RETRY_ATTEMPTS) {
            scheduleServerSuccessRetry();
          } else {
            stopAutoRetry();
          }
        });
      } else {
        if (res && res.registered) {
          stopAutoRetry();
        } else if (autoRetryAttempts < MAX_RETRY_ATTEMPTS) {
          scheduleServerSuccessRetry();
        } else {
          stopAutoRetry();
        }
      }
      return res;
    } catch (e) {}
    return null;
  }

  /**
   * Official loader pattern for Shopify Customer Privacy API
   */
  function initPrivacyApi() {
    function handleConsentUpdate() {
      recordTouchpoint();
      checkServerSuccess();
    }

    if (typeof document !== 'undefined') {
      document.addEventListener('visitorConsentCollected', handleConsentUpdate);
    }

    if (typeof window !== 'undefined' && window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1'
          }
        ],
        function (error) {
          if (error) {
            purgeStoredAttribution();
            return;
          }
          handleConsentUpdate();
        }
      );
    } else {
      handleConsentUpdate();
    }
  }

  // Self-initialize privacy & touchpoint on load
  initPrivacyApi();

  // BFCache (back-forward cache) listener
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('pageshow', function (evt) {
      if (evt && evt.persisted) {
        checkServerSuccess();
      }
    });
  }

  return {
    init: function () {
      recordTouchpoint();
      checkServerSuccess();
    },
    initPrivacyApi: initPrivacyApi,
    getAttributionPayload: getAttributionPayload,
    getOrCreateLeadId: getOrCreateLeadId,
    clearPendingLead: clearPendingLead,
    populateForm: populateForm,
    formatAttributionSummary: formatAttributionSummary,
    purgeStoredAttribution: purgeStoredAttribution,
    inspectConsentState: inspectConsentState,
    publishLeadSubmitted: publishLeadSubmitted,
    checkServerSuccess: checkServerSuccess,
    stopAutoRetry: stopAutoRetry,
    getRetryAttempts: function () { return autoRetryAttempts; },
    hasActiveRetryTimer: function () { return autoRetryTimer !== null; },
    SCHEMA_VERSION: SCHEMA_VERSION
  };
}));
